import type { PostgrestError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import type { Enums, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { requireRowsWritten } from "@/lib/rowsWritten";

const todayISO = () => new Date().toISOString().slice(0, 10);

// ── Reads ──

export function fetchEmployees(storeId: string) {
  return supabase
    .from("employees")
    .select("*")
    .eq("store_id", storeId)
    .order("planning_display_order")
    .order("last_name");
}

/**
 * Salariés encore sous contrat à la date `today` : ceux sans date de sortie, mais aussi
 * ceux dont la sortie est encore à venir.
 *
 * Un CDD porte sa date de fin dès sa création : filtrer sur `exit_date IS NULL` excluait
 * donc tous les CDD en cours. C'est ce qui rendait un salarié déjà fiché invisible dans
 * le sélecteur de l'assistant contrat, et poussait à repasser par « Nouveau salarié » —
 * la façon exacte dont un doublon se crée.
 */
export function fetchActiveEmployees(storeId: string, today: string = todayISO()) {
  return supabase
    .from("employees")
    .select("*")
    .eq("store_id", storeId)
    .or(`exit_date.is.null,exit_date.gte.${today}`)
    .order("planning_display_order")
    .order("last_name");
}

export function fetchEmployeeByUserId(userId: string) {
  return supabase
    .from("employees")
    .select("store_id, stores(id, name)")
    .eq("user_id", userId)
    .maybeSingle();
}

export function fetchEmployeeWithStoreInfo(userId: string) {
  return supabase
    .from("employees")
    .select("*, stores(name, subscription_status)")
    .eq("user_id", userId)
    .maybeSingle();
}

export function fetchEmployeesForStore(storeId: string, select: string) {
  return supabase
    .from("employees")
    .select(select as "*")
    .eq("store_id", storeId);
}

export function fetchEmployeesWithUserIdPartial(storeId: string) {
  return supabase
    .from("employees")
    .select("id, user_id, entry_date, exit_date, first_name, last_name")
    .eq("store_id", storeId)
    .not("user_id", "is", null);
}

export function fetchEmployeesPartialWithEmail(storeId: string) {
  return supabase
    .from("employees")
    .select("id, first_name, last_name, email, user_id, entry_date, exit_date")
    .eq("store_id", storeId)
    .not("user_id", "is", null);
}

export function fetchEmployeeCount(storeId: string) {
  return supabase
    .from("employees")
    .select("id", { count: "exact", head: true })
    .eq("store_id", storeId);
}

/**
 * Fiches du magasin liées à un compte, avec la date d'activation de ce compte (RPC
 * `get_store_employee_activations`, migration 20261101203000, en prod depuis le 01/11/2026).
 * `employees.user_id` ne le dit pas : l'invitation crée le compte et lie la fiche dès l'envoi.
 * `activated_at` vaut `null` tant que le lien d'activation n'a pas été ouvert, bien que le type
 * généré l'annonce non nul.
 */
export function fetchEmployeeActivations(storeId: string) {
  return supabase.rpc("get_store_employee_activations", { p_store_id: storeId });
}

export function fetchInvitedEmployeeCount(storeId: string) {
  return supabase
    .from("employees")
    .select("id", { count: "exact", head: true })
    .eq("store_id", storeId)
    .not("invited_at", "is", null);
}

export function fetchFutureScheduleCount(employeeId: string, afterDate: string) {
  return supabase
    .from("schedules")
    .select("id", { count: "exact", head: true })
    .eq("employee_id", employeeId)
    .gt("date", afterDate);
}

/** Compte les créneaux passés (date <= onOrBeforeDate) — sert à protéger la suppression définitive. */
export function fetchPastScheduleCount(employeeId: string, onOrBeforeDate: string) {
  return supabase
    .from("schedules")
    .select("id", { count: "exact", head: true })
    .eq("employee_id", employeeId)
    .lte("date", onOrBeforeDate);
}

/** Compte les créneaux sur [startDateInclusive, endDateExclusive[ (pour prévisualiser une suppression). */
export function fetchScheduleCountInDateRange(
  employeeId: string,
  startDateInclusive: string,
  endDateExclusive: string,
) {
  return supabase
    .from("schedules")
    .select("id", { count: "exact", head: true })
    .eq("employee_id", employeeId)
    .gte("date", startDateInclusive)
    .lt("date", endDateExclusive);
}

export function deleteFutureSchedules(employeeId: string, afterDate: string) {
  return supabase
    .from("schedules")
    .delete()
    .eq("employee_id", employeeId)
    .gt("date", afterDate);
}

/**
 * Pose la date de sortie, supprime les créneaux postérieurs et révoque les congés postérieurs,
 * atomiquement (RPC transactionnelle mark_employee_exit). Renvoie les compteurs.
 */
export function markEmployeeExit(employeeId: string, exitDate: string) {
  return (supabase.rpc as unknown as (
    fn: string,
    args: Record<string, unknown>,
  ) => Promise<{ data: { schedules_deleted?: number; leaves_revoked?: number } | null; error: unknown }>)(
    "mark_employee_exit",
    { p_employee_id: employeeId, p_exit_date: exitDate },
  );
}

/** Révoque les demandes de congés (pending/approved) postérieures à une date pour un salarié. */
export function revokeLeaveRequestsAfter(employeeId: string, afterDate: string) {
  return supabase
    .from("leave_requests")
    .update({ status: "revoked" })
    .eq("employee_id", employeeId)
    .gt("start_date", afterDate)
    .in("status", ["pending", "approved"]);
}

/**
 * Supprime tous les créneaux/absences/CP/etc sur [startDateInclusive, endDateExclusive[
 * (dates au format ISO yyyy-MM-dd ; comparaison lexicographique fiable).
 */
export function deleteSchedulesInDateRange(
  employeeId: string,
  startDateInclusive: string,
  endDateExclusive: string,
) {
  return supabase
    .from("schedules")
    .delete()
    .eq("employee_id", employeeId)
    .gte("date", startDateInclusive)
    .lt("date", endDateExclusive);
}

export function fetchDashboardEmployees(storeId: string) {
  return supabase
    .from("employees")
    .select("id, first_name, last_name, probation_end_date, exit_date, contract_type, entry_date, weekly_contract_hours, is_part_time, dpae_status")
    .eq("store_id", storeId);
}

export function fetchEmployeesOrderedByLastName(storeId: string) {
  return supabase
    .from("employees")
    .select("*")
    .eq("store_id", storeId)
    .order("last_name");
}

/** Collègue tel que le planning équipe du portail salarié le reçoit. */
export interface TeamPlanningMember {
  id: string;
  first_name: string;
  last_name: string;
  job_title: string;
  /** Date d'entrée si elle tombe dans la semaine demandée, `null` sinon. */
  entry_date: string | null;
  /** Date de sortie si elle tombe dans la semaine demandée, `null` sinon. */
  exit_date: string | null;
}

/**
 * Équipe du planning d'une semaine pour le portail salarié (RPC `get_team_planning_members`,
 * migration 20261103100000) : les collègues sous contrat cette semaine-là, du magasin du salarié
 * connecté, triés par nom, sans rien d'autre de leur fiche que ce que le planning affiche. La RLS
 * de `employees` ne montre plus au salarié que sa propre fiche. Le résultat est annoncé en
 * `TeamPlanningMember` : le type généré donne `entry_date` et `exit_date` non nuls, alors que la
 * fonction les renvoie à `null` hors de la semaine demandée.
 */
export function fetchTeamPlanningMembers(
  weekStart: string,
): PromiseLike<{ data: TeamPlanningMember[] | null; error: PostgrestError | null }> {
  return supabase.rpc("get_team_planning_members", { p_week_start: weekStart });
}

const DUPLICATE_CANDIDATE_SELECT =
  "id, first_name, last_name, birth_date, entry_date, exit_date, contract_type";

/** Fiche déjà présente en base qui pourrait désigner la même personne. */
export type DuplicateEmployeeCandidate = {
  id: string;
  first_name: string;
  last_name: string;
  birth_date: string | null;
  entry_date: string;
  exit_date: string | null;
  contract_type: Enums<"contract_type">;
};

/**
 * Recherche les fiches du magasin susceptibles de désigner la même personne :
 * même prénom + nom (insensible à la casse), ou même date de naissance.
 *
 * Les fiches **sorties sont volontairement incluses** : recréer une fiche pour quelqu'un
 * qui vient d'être sorti est précisément le geste qui produit un doublon — le planning
 * affiche alors deux lignes pour la même personne sur la semaine de la sortie, l'une
 * vide et l'autre remplie, et le contrat reste accroché à la mauvaise fiche.
 *
 * La vérification est faite en base et non via le cache React Query : elle reste juste
 * malgré un cache périmé, et couvre le retry après une coupure réseau survenue entre le
 * POST de création et sa réponse (la fiche existe déjà côté serveur).
 */
export async function findPotentialDuplicateEmployees(
  storeId: string,
  candidate: { firstName: string; lastName: string; birthDate?: string | null },
): Promise<{ data: DuplicateEmployeeCandidate[] | null; error: PostgrestError | null }> {
  const firstName = candidate.firstName.trim();
  const lastName = candidate.lastName.trim();
  const birthDate = candidate.birthDate?.trim() || null;

  const queries = [];
  if (firstName && lastName) {
    queries.push(
      supabase
        .from("employees")
        .select(DUPLICATE_CANDIDATE_SELECT)
        .eq("store_id", storeId)
        .ilike("first_name", firstName)
        .ilike("last_name", lastName),
    );
  }
  if (birthDate) {
    queries.push(
      supabase
        .from("employees")
        .select(DUPLICATE_CANDIDATE_SELECT)
        .eq("store_id", storeId)
        .eq("birth_date", birthDate),
    );
  }
  if (queries.length === 0) return { data: [], error: null };

  const results = await Promise.all(queries);
  const failed = results.find((result) => result.error);
  if (failed?.error) return { data: null, error: failed.error };

  // Une fiche peut remonter par les deux critères à la fois : on dédoublonne par id.
  const byId = new Map<string, DuplicateEmployeeCandidate>();
  for (const result of results) {
    for (const row of result.data ?? []) byId.set(row.id, row as DuplicateEmployeeCandidate);
  }
  return { data: [...byId.values()], error: null };
}

// ── Writes ──

export function insertEmployee(data: TablesInsert<"employees">) {
  return supabase
    .from("employees")
    .insert(data)
    .select("*")
    .single();
}

export function updateEmployee(employeeId: string, data: TablesUpdate<"employees">) {
  return supabase
    .from("employees")
    .update(data)
    .eq("id", employeeId);
}

export function deleteEmployee(employeeId: string) {
  return supabase
    .from("employees")
    .delete()
    .eq("id", employeeId);
}

/** La RLS réserve la modification des fiches au gérant et au Directeur : l'Adjoint est refusé sans erreur. */
export function updateEmployeeDisplayOrder(employeeId: string, order: number) {
  return requireRowsWritten(
    supabase
      .from("employees")
      .update({ planning_display_order: order })
      .eq("id", employeeId)
      .select("id"),
    "Seuls le gérant et le Directeur changent l'ordre des salariés sur le planning.",
  );
}

export function fetchEmployeeFields(employeeId: string, select: string) {
  return supabase
    .from("employees")
    .select(select as "*")
    .eq("id", employeeId)
    .single();
}

export function insertEmployeeReturningId(data: TablesInsert<"employees">) {
  return supabase
    .from("employees")
    .insert(data)
    .select("id")
    .single();
}
